// src/pages/EntryDetail.tsx
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Tag } from 'lucide-react';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import { getLogEntry, BuildLogEntry } from '../services/firebase';
import { AppLayout } from '../components/layout/AppLayout';

export function EntryDetail() {
  const { projectId, entryId } = useParams<{ projectId: string; entryId: string }>();
  const navigate = useNavigate();
  const [entry, setEntry] = useState<BuildLogEntry | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (entryId) loadEntry();
  }, [entryId]);

  const loadEntry = async () => {
    if (!entryId) return;
    try {
      const data = await getLogEntry(entryId);
      setEntry(data);
      if (data) document.title = `Buildlog — ${data.title}`;
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <AppLayout>
      <div className="flex items-center justify-center py-40">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-brand-blue border-t-transparent" />
      </div>
    </AppLayout>
  );

  if (!entry) return (
    <AppLayout>
      <div className="text-center py-40">
        <p className="text-red-500 mb-4">Entry not found</p>
        <button
          onClick={() => navigate(`/projects/${projectId}`)}
          className="text-brand-blue hover:underline"
        >
          Back to Project
        </button>
      </div>
    </AppLayout>
  );

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto">
        <Link
          to={`/projects/${projectId}`}
          className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900 mb-8 transition-colors"
        >
          <ArrowLeft size={16} className="mr-1" />
          Back to Project
        </Link>

        <article>
          <header className="mb-8 pb-8 border-b border-slate-200">
            <h1
              className="text-4xl font-bold text-slate-900 mb-4 leading-tight"
            >
              {entry.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-slate-500 text-sm font-mono">
              <div className="flex items-center gap-2">
                <Calendar size={16} />
                <time>{format(entry.timestamp.toDate(), 'MMMM d, yyyy')}</time>
              </div>
              {entry.tags && entry.tags.length > 0 && (
                <div className="flex items-center gap-2">
                  <Tag size={16} />
                  <div className="flex gap-2 flex-wrap">
                    {entry.tags.map(tag => (
                      <span key={tag}>#{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </header>

          <div className="prose prose-slate prose-lg max-w-none font-sans prose-headings:font-display prose-code:font-mono prose-pre:bg-slate-900 prose-pre:border prose-pre:border-slate-800">
            <ReactMarkdown>{entry.content}</ReactMarkdown>
          </div>

          <div className="mt-12 pt-8 border-t border-slate-200">
            <div className="text-sm text-slate-400 font-mono">
              ID: {entry.id} · Project: {entry.projectId}
            </div>
          </div>
        </article>
      </div>
    </AppLayout>
  );
}